import { Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'

export default function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 to-blue-100">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                AI Lead Capture Chatbot for Real Estate Agents
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Capture leads while you sleep. Qualify buyers and sellers automatically. Book appointments 24/7.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/configure" className="btn-primary text-center">
                  Get Started Now
                </Link>
                <Link to="/pricing" className="btn-secondary text-center">
                  View Pricing
                </Link>
              </div>
              <p className="mt-6 text-sm text-gray-600">
                $297 setup + $297/month • Deploy in 5 minutes • No coding required
              </p>
            </div>

            {/* Demo/Illustration */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-primary-200">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary-600 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white font-bold">AI</span>
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-lg p-4">
                      <p className="text-sm">Hi! Are you looking to buy or sell a home?</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 flex-row-reverse">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0"></div>
                    <div className="flex-1 bg-primary-600 text-white rounded-lg p-4">
                      <p className="text-sm">I'm looking to buy!</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary-600 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white font-bold">AI</span>
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-lg p-4">
                      <p className="text-sm">Great! What's your price range?</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Badge */}
              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-6 py-3 rounded-full shadow-lg font-semibold">
                24/7 Active
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Capture Leads
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your AI assistant works 24/7, qualifying leads and booking appointments so you can focus on closing deals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Availability</h3>
              <p className="text-gray-600 leading-relaxed">
                Never miss a lead again. Your chatbot is always online, capturing leads even when you're sleeping, showing houses, or on vacation.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">HOT Lead Detection</h3>
              <p className="text-gray-600 leading-relaxed">
                AI automatically identifies pre-approved buyers and motivated sellers, flagging them as HOT leads for immediate follow-up.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Calendly Integration</h3>
              <p className="text-gray-600 leading-relaxed">
                Automatically book appointments directly to your calendar. No back-and-forth emails. Just confirmed meetings with qualified leads.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Instant Notifications</h3>
              <p className="text-gray-600 leading-relaxed">
                Get email alerts every time a new lead comes in. Never let a hot prospect slip through the cracks.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Custom Branding</h3>
              <p className="text-gray-600 leading-relaxed">
                Make it yours. Customize colors, logo, and agent name to match your brand. White-labeled for your business.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Analytics Dashboard</h3>
              <p className="text-gray-600 leading-relaxed">
                Track your leads, conversion rates, and performance. Data-driven insights to optimize your lead capture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Deploy Your Chatbot in 5 Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No coding required. No technical knowledge needed. Just answer a few questions and you're live.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Configure Your Bot</h3>
              <p className="text-gray-600">
                Enter your name, Calendly link, and market preferences. Takes 2 minutes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Add to Your Website</h3>
              <p className="text-gray-600">
                Copy-paste one line of code to your website. We provide step-by-step instructions.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Start Capturing Leads</h3>
              <p className="text-gray-600">
                Your chatbot goes live immediately. Start receiving qualified leads 24/7.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary-50 to-blue-50 rounded-3xl p-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Just One Deal Pays for a Year
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Average real estate commission: <span className="font-bold text-primary-600">$10,000</span><br />
              HellaBots annual cost: <span className="font-bold">$3,861</span><br />
              <span className="text-2xl font-bold text-primary-600">ROI: 259%</span>
            </p>
            <p className="text-lg text-gray-600 mb-8">
              If this chatbot books you just <span className="font-bold">ONE deal per year</span>, you're profitable. Most agents close 2-3+ deals from their chatbot leads annually.
            </p>
            <Link to="/configure" className="btn-primary inline-block">
              Get Started - Risk Free
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Do I need to know how to code?</h3>
              <p className="text-gray-600">
                Not at all! We provide simple copy-paste code that goes on your website. If you can update your website's HTML, you can add HellaBots.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How long does setup take?</h3>
              <p className="text-gray-600">
                Most agents are live in under 5 minutes. Configuration takes 2 minutes, and adding the code to your website takes another 1-2 minutes.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Can I customize the chatbot?</h3>
              <p className="text-gray-600">
                Yes! You can customize your bot's name, colors, logo, and configure it for your specific market areas and price ranges.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What if I want to cancel?</h3>
              <p className="text-gray-600">
                You can cancel anytime, no questions asked. There are no long-term contracts or commitments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section bg-primary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Ready to Capture Leads 24/7?
          </h2>
          <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
            Join successful real estate agents who never miss a lead. Get started in 5 minutes.
          </p>
          <Link to="/configure" className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl inline-block text-lg">
            Get Started Now
          </Link>
          <p className="mt-6 text-primary-100">
            $297 setup + $297/month • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  )
}
